import type { EncodedImage } from '@discovr/features';
import type { ThreadFileKind } from '@discovr/types';

import { createCanvas2d } from '../browser/canvas';
import { readImageSize, readIsAnimated, type ImageSize } from './imageHeader';

/**
 * Shared by every picked-file and captured-frame upload path so a photo and a
 * video snapshot compress to the exact same rule: longest side 1600px, WebP
 * at quality 0.75, JPEG where the browser cannot encode WebP.
 */
export const IMAGE_MAX_LONG_EDGE = 1600;
export const IMAGE_QUALITY = 0.75;
/** The file kind every send compresses before upload. */
export const COMPRESSIBLE_KIND: ThreadFileKind = 'image';
/**
 * How far over the per-file limit a picked image may be: its upload is the
 * compressed copy, which lands far below the limit. Bounded so the browser
 * is never asked to decode an arbitrarily large file.
 */
export const COMPRESSIBLE_PICK_SIZE_FACTOR = 4;
/**
 * The backend's image policy (the file policy doesn't carry it): a side over
 * this, or more pixels than this, is refused — and decoding one first would
 * cost the browser a bitmap of hundreds of megabytes.
 */
export const IMAGE_MAX_SIDE_PX = 8192;
export const IMAGE_MAX_PIXELS = 25_000_000;
/** Error code of an image refused here, before decoding, for being over those limits. */
export const IMAGE_TOO_LARGE_CODE = 'IMAGE_TOO_LARGE';
/** What transparent pixels become in a JPEG, which has no alpha channel. */
export const JPEG_BACKGROUND = '#FFFFFF';

/** `CompressedImage` is the UI package's name for the send pipeline's `EncodedImage` shape. */
export type CompressedContentType = EncodedImage['contentType'];
export type CompressedImage = EncodedImage;

export interface CompressImageOptions {
  maxLongEdge?: number;
  quality?: number;
}

const WEBP_CONTENT_TYPE = 'image/webp' satisfies CompressedContentType;
const JPEG_CONTENT_TYPE = 'image/jpeg' satisfies CompressedContentType;

/** The file extension for each format the encoder writes — an upload's name must match its bytes. */
export const ENCODED_IMAGE_EXTENSION: Record<CompressedContentType, string> = {
  [WEBP_CONTENT_TYPE]: '.webp',
  [JPEG_CONTENT_TYPE]: '.jpg',
};

/** Whether `contentType` is one of the formats the encoder writes. */
export function isCompressedContentType(contentType: string | undefined): contentType is CompressedContentType {
  return contentType === WEBP_CONTENT_TYPE || contentType === JPEG_CONTENT_TYPE;
}

export class ImageTooLargeError extends Error {
  constructor(readonly size: ImageSize) {
    super(`image is ${size.width}x${size.height}, over the ${IMAGE_MAX_SIDE_PX}px side / ${IMAGE_MAX_PIXELS}px area limits`);
    this.name = 'ImageTooLargeError';
  }
}

/** Whether `size` is beyond what the backend accepts. */
export function exceedsImageLimits({ width, height }: ImageSize): boolean {
  return width > IMAGE_MAX_SIDE_PX || height > IMAGE_MAX_SIDE_PX || width * height > IMAGE_MAX_PIXELS;
}

/** Safari holds a canvas's backing store until it is collected; zeroing it gives the memory back at once. */
export function releaseCanvas(canvas: HTMLCanvasElement): void {
  canvas.width = 0;
  canvas.height = 0;
}

function encodeAs(canvas: Pick<HTMLCanvasElement, 'toBlob'>, contentType: CompressedContentType, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, contentType, quality));
}

/**
 * A copy of `source` over `JPEG_BACKGROUND`. A JPEG encoder turns every
 * transparent pixel black, so a picked PNG with transparency would upload on
 * a black background.
 */
function onOpaqueBackground(source: HTMLCanvasElement): HTMLCanvasElement {
  const { width, height } = source;
  const { canvas, ctx } = createCanvas2d(width, height);
  ctx.fillStyle = JPEG_BACKGROUND;
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0);
  return canvas;
}

/**
 * Encode the canvas as WebP, falling back to JPEG. A browser that cannot
 * encode the requested type silently hands back a PNG, so the blob's own
 * type — not the request — decides which format was produced. WebP keeps the
 * canvas's transparency; the JPEG is encoded from an opaque copy. Throws when
 * neither format can be encoded.
 */
export async function encodeCanvas(canvas: HTMLCanvasElement, quality: number = IMAGE_QUALITY): Promise<CompressedImage> {
  const { width, height } = canvas;
  const webp = await encodeAs(canvas, WEBP_CONTENT_TYPE, quality);
  if (webp?.type === WEBP_CONTENT_TYPE) return { blob: webp, contentType: WEBP_CONTENT_TYPE, width, height };
  const opaque = onOpaqueBackground(canvas);
  const jpeg = await encodeAs(opaque, JPEG_CONTENT_TYPE, quality).finally(() => releaseCanvas(opaque));
  if (jpeg?.type === JPEG_CONTENT_TYPE) return { blob: jpeg, contentType: JPEG_CONTENT_TYPE, width, height };
  throw new Error('canvas could not encode the image');
}

/** Pure: a width/height pair scaled down so its long edge is at most `maxLongEdge`; unchanged if already within it. */
export function fitWithin(width: number, height: number, maxLongEdge: number): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxLongEdge) return { width, height };
  const scale = maxLongEdge / longEdge;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * A picked image file → resized and encoded per the shared compression rule.
 * Resolves `null` when the browser can't decode the file rather than
 * throwing, or when it is animated (a canvas would flatten it to one frame),
 * so callers can fall back to the original. Throws
 * `ImageTooLargeError` — before decoding anything — for one whose header
 * says it is over the backend's size limits.
 */
export async function compressImageFile(file: Blob, opts: CompressImageOptions = {}): Promise<CompressedImage | null> {
  const maxLongEdge = opts.maxLongEdge ?? IMAGE_MAX_LONG_EDGE;
  const quality = opts.quality ?? IMAGE_QUALITY;

  const headerSize = await readImageSize(file);
  if (headerSize && exceedsImageLimits(headerSize)) throw new ImageTooLargeError(headerSize);

  if (await readIsAnimated(file)) return null;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }

  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, maxLongEdge);
    const { canvas, ctx } = createCanvas2d(width, height);
    try {
      ctx.drawImage(bitmap, 0, 0, width, height);
      return await encodeCanvas(canvas, quality);
    } finally {
      releaseCanvas(canvas);
    }
  } finally {
    bitmap.close();
  }
}

/** Keep whichever is smaller; the original keeps its own type — never re-encoded, never re-typed. */
export function pickSmaller(original: Blob, compressed: CompressedImage): Blob {
  return compressed.blob.size < original.size ? compressed.blob : original;
}
